import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import villaImage from "../images/Container.svg";
import chaletImage from "../images/Tooltip.png";
import editIcon from "../images/eye.svg";
import editPenIcon from "../images/edit.svg";
import deleteIcon from "../images/trash-xmark.svg";
import chevronLeft from "../images/chevron-left-outlined-rounded.svg";
import chevronRight from "../images/chevron-right-outlined-rounded.svg";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";
import { BASE_URL } from "../utils/config";
import { useTranslation } from "react-i18next";
import { useFlow } from "../context/FlowContext";
import { useAuth } from "../context/AuthContext";
import styles from "./MyProperties.module.css";
import NewSearchesSwiper from "../components/NewSearchesSwiper";
import { useNavigate } from "react-router-dom";
import BulkUploadModal from "../components/BulkUploadModal";

const MyProperties = () => {
  // Helper function to capitalize the first letter of a string
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Helper function to format numbers with apostrophes (Swiss format)
  const formatWithApostrophe = (value) => {
    if (!value && value !== 0) return '';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/'/g, '')) : value;
    if (isNaN(numValue)) return value;
    const digitsOnly = numValue.toString().replace(/[^\d]/g, '');
    return digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  };

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentFlow } = useFlow();
  const { currentUser } = useAuth();
  const [allProperties, setAllProperties] = useState([]);
  
  // Determine if user is offer or demand/search user
  const isOfferUser = currentFlow === "offer" || currentUser?.userType === "offer";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [propertyToDeleteId, setPropertyToDeleteId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("for-sale");
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sliderKey, setSliderKey] = useState(Date.now());
  const [searchQuery, setSearchQuery] = useState("");
  const [filterZipcode, setFilterZipcode] = useState("");
  const [filterSqm, setFilterSqm] = useState("");
  const [filterBedrooms, setFilterBedrooms] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterPrice, setFilterPrice] = useState("");
  const [sortColumn, setSortColumn] = useState(""); // Which column to sort by: "name", "zipcode", "sqm", "bedrooms", "city", "type", "price"
  const [sortOrder, setSortOrder] = useState("asc"); // "asc" or "desc"
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const response = await authFetch(
          `${BASE_URL}/property/latest/offers?limit=30`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch offers");
        }

        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          const data = await response.json();
          // Handle both { data: [...] } and [...] response formats, and remove duplicates
          const offersArray = Array.isArray(data) ? data : (data?.data || []);
          // Filter out properties where deletedAt is not null and remove duplicates
          const filteredData = offersArray.filter(item => 
            (!item.deletedAt || item.deletedAt === null) && item && item.id
          );
          const uniqueOffers = filteredData.filter((item, index, self) =>
            index === self.findIndex((t) => t.id === item.id)
          );
          setOffers(uniqueOffers);
          setSliderKey(Date.now());
        } else {
          const text = await response.text();
          throw new Error(
            `Server returned a non-JSON response. Check your API route. Response was: "${text.substring(
              0,
              50
            )}..."`
          );
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching offers:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOffers();

    // const intervalId = setInterval(fetchOffers, 60000);

    return () => {
      // Polling disabled to prevent property changes during animation
      // clearInterval(intervalId);
    };
  }, []);

  const fetchProperties = async (page = 1, limit = itemsPerPage, offeringType = null) => {
    try {
      // Build query parameters with filters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (offeringType) params.append('offeringType', offeringType);
      if (searchQuery) params.append('searchQuery', searchQuery);
      if (filterZipcode) params.append('zipcode', filterZipcode);
      if (filterCity) params.append('city', filterCity);
      if (filterType) params.append('propertyType', filterType);
      if (filterSqm) params.append('minSqm', filterSqm);
      if (filterBedrooms) params.append('bedrooms', filterBedrooms);
      if (filterPrice) params.append('minPrice', filterPrice);

      const response = await authFetch(
        `${BASE_URL}/property/user-properties?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setAllProperties(data.data || []); // Store backend-filtered properties
      setTotalCount(data.total || 0); // Store total filtered count from backend
    } catch (err) {
      console.error("Failed to fetch properties:", err);
      setError(t("error_load_properties_message")); // Translated
      toast.error(t("error_load_properties_toast")); // Translated
    } finally {
      setLoading(false);
    }
  };

  // Fetch properties when tab, page, itemsPerPage, or any filter changes (with debouncing for filters)
  useEffect(() => {
    const offeringType = activeTab === "for-sale" ? "purchase" : "rental";
    const isFilterChange = searchQuery || filterZipcode || filterCity || filterType || filterSqm || filterBedrooms || filterPrice;

    if (!isFilterChange) {
      // No filters applied, fetch immediately
      fetchProperties(currentPage, itemsPerPage, offeringType);
      return;
    }

    // Debounce filter changes
    const debounceTimer = setTimeout(() => {
      fetchProperties(currentPage, itemsPerPage, offeringType);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(debounceTimer);
  }, [activeTab, currentPage, itemsPerPage, searchQuery, filterZipcode, filterSqm, filterBedrooms, filterCity, filterType, filterPrice]);

  // Reset to first page when filters change (but not on initial mount)
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchQuery, filterZipcode, filterSqm, filterBedrooms, filterCity, filterType, filterPrice]);

  // Handle subscription success from URL parameter (keep existing logic)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("success");

    if (success) {
      const pending = localStorage.getItem("pendingSubscription");

      if (pending) {
        const { planId } = JSON.parse(pending);

        authFetch(`${BASE_URL}/subscription/subscribe`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            planId,
            paymentMethod: "stripe",
            platform: "web",
          }),
        })
          .then((res) => res.json())
          .then((data) => {
            console.log("✅ Subscription recorded:", data);
            // toast.success(t('subscription_success_toast')); // Translated
          })
          .catch((err) => {
            console.error("❌ Failed to record subscription:", err);
            toast.error(t("subscription_fail_toast")); // Translated
          });

        localStorage.removeItem("pendingSubscription");
      }

      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleDelete = (id) => {
    setPropertyToDeleteId(id);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    setShowConfirmModal(false); // Close the modal first
    if (!propertyToDeleteId) return; // Guard clause: ensure there's an ID to delete

    try {
      toast.info(t("deleting_property_toast")); // Translated
      const response = await authFetch(
        `${BASE_URL}/property/${propertyToDeleteId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t("failed_delete_property_error")); // Translated
      }

      const result = await response.json();
      console.log("Delete successful:", result);

      // Update both allProperties and filteredProperties
      setAllProperties((prev) =>
        prev.filter((property) => property.id !== propertyToDeleteId)
      );
      toast.success(t("property_deleted_toast")); // Translated
    } catch (error) {
      console.error("Error deleting property:", error);
      toast.error(`${t("error_deleting_property_toast")}: ${error.message}`); // Translated
    } finally {
      setPropertyToDeleteId(null); // Clear the ID after the action (success or failure)
    }
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setPropertyToDeleteId(null); // Clear the ID
  };

  // Backend handles filtering and pagination - just display the results
  const currentProperties = allProperties;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const handleNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  // Tab change handler
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to first page when switching tabs
  };

  const handleAddPropertyClick = () => {
    // Navigate to OfferProperty with type parameter based on active tab
    if (activeTab === "for-rent") {
      navigate("/OfferProperty?type=rental");
    } else {
      // Default to purchase (sale) for "for-sale" tab
      navigate("/OfferProperty?type=purchase");
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setFilterType("");
    setFilterCity("");
    setFilterZipcode("");
    setFilterSqm("");
    setFilterBedrooms("");
    setFilterPrice("");
    setSortColumn("");
    setSortOrder("asc");
  };

  // Filter out properties where deletedAt is not null before displaying
  const validOffers = offers.filter(item => !item.deletedAt || item.deletedAt === null);
  const displayedOffers = validOffers.slice(0, 3);

  return (
    <div className={styles.container}>
      <div className={styles.leftPanel}>
        {!isLoading && !error && offers.length > 0 && (
          <div className={styles.newSearchesHeader}>
            <h2 className={styles.newSearchesTitle}>{t("new_offers")}</h2>
          </div>
        )}

        {displayedOffers.length > 0 && (
          <div className={styles.propertyList}>
            <NewSearchesSwiper items={displayedOffers} className={styles.mySwiper} />
          </div>
        )}
      </div>

      <div className={`${styles.rightPanel} ${styles.rightPanelMarginLeft}`}>
        <h2 className={styles.pageHeader}>{t("listed_properties_heading")}</h2>

        <div className={styles.tabsContainer}>
          <div className={styles.tabsGroup}>
            <button
              className={`${styles.tabButton} ${
                activeTab === "for-sale" ? styles.activeTab : styles.inactiveTab
              } ${styles.borderTopLeftRadius}`}
              onClick={() => handleTabChange("for-sale")}
            >
              {t("for_sale_tab")}
            </button>
            <button
              className={`${styles.tabButton} ${
                activeTab === "for-rent" ? styles.activeTab : styles.inactiveTab
              } ${styles.borderTopRightRadius}`}
              onClick={() => handleTabChange("for-rent")}
            >
              {t("for_rental_tab")}
            </button>
          </div>
          <div className={styles.searchAndAddGroup}>
            <input
              type="text"
              placeholder={t("search_all_columns")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.headerSearchInput}
            />
            <button
              className={styles.addLocationButton}
              onClick={handleAddPropertyClick}
            >
              +
            </button>
          </div>
        </div>

        {/* Filter and Sort Bar */}
        <div className={styles.filterBar}>
          {/* <input
            type="text"
            placeholder={t("search_by_property_title_type") || "Search by property title, type"}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchFilterInput}
          /> */}

         

       

          <input
            type="text"
            placeholder={t("filter_by_zipcode") || "Filter by zip code..."}
            value={filterZipcode}
            onChange={(e) => setFilterZipcode(e.target.value)}
            className={styles.filterTextInput}
          />

          <input
            type="number"
            placeholder={t("filter_by_sqm") || "Filter by sqm..."}
            value={filterSqm}
            onChange={(e) => setFilterSqm(e.target.value)}
            className={styles.filterTextInput}
          />

          <input
            type="number"
            placeholder={t("Bedrooms_Units_Index") || "Bedrooms/Units/Index"}
            value={filterBedrooms}
            onChange={(e) => setFilterBedrooms(e.target.value)}
            className={styles.filterTextInput}
          />
             <input
            type="text"
            placeholder={t("filter_by_city") || "Filter by city..."}
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            className={styles.filterTextInput}
          />

<select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className={styles.filterDropdown}
          >
            <option value="">{t("property_any")}</option>
            <option value="house/villa">{t("house/villa") || "House/Villa"}</option>
            <option value="apartment">{t("apartment") || "Apartment"}</option>
            <option value="multifamily">{t("multifamily") || "Multifamily"}</option>
            <option value="plot">{t("plot") || "Plot"}</option>
          </select>

          <input
            type="number"
            placeholder={t("filter_by_price") || "Filter by price..."}
            value={filterPrice}
            onChange={(e) => setFilterPrice(e.target.value)}
            className={styles.filterTextInput}
          />

          

          <select
            value={sortColumn}
            onChange={(e) => setSortColumn(e.target.value)}
            className={styles.filterDropdown}
          >
            <option value="">{t("sort_by") || "Sort by"}</option>
            <option value="name">{t("table_header_offer") || "Offer"}</option>
            <option value="title">{t("table_header_title") || "Title"}</option>
            <option value="zipcode">{t("zip-code") || "Zip Code"}</option>
            <option value="sqm">{t("Sqm") || "sqm"}</option>
            <option value="bedrooms">{t("Bedrooms") || "Bedrooms"}</option>
            <option value="city">{t("table_header_city") || "City"}</option>
            <option value="type">{t("table_header_type") || "Type"}</option>
            <option value="price">{t("table_header_price") || "Price"}</option>
          </select>

          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className={styles.filterDropdown}
            disabled={!sortColumn}
          >
            <option value="asc">{t("ascending") || "Ascending"}</option>
            <option value="desc">{t("descending") || "Descending"}</option>
          </select>

          <button
            className={styles.clearButton}
            onClick={handleClearFilters}
          >
            {t("clear_filters") || "Clear Filters"}
          </button>
          {isOfferUser && (
            <button
              className={styles.bulkUploadButton}
              onClick={() => setShowBulkUploadModal(true)}
              title={t("bulk_upload_properties") || "Bulk Upload Properties"}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: "5px" }}
              >
                <path
                  d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7 10L12 5L17 10"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 5V15"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t("bulk_upload") || "Bulk Upload"}
            </button>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '15px', marginTop: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label htmlFor="rowsPerPage">{t("rows_per_page") || "Rows per page"}:</label>
            <select
              id="rowsPerPage"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1); // Reset to first page when changing rows per page
              }}
              className={styles.rowsPerPageSelect}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        <div style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
          <div className={styles.tableContainer}>
            <table className={styles.searchTable}>
            <thead>
              <tr>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>
                  {" "}
                  {t("property_id")}
                </th>
                {/* <th className={styles.tableHeader}>
                  {" "}
                  {t("Id")}
                </th> */}
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>
                  {" "}
                  {t("table_header_title")}
                </th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>
                  {" "}
                  {t("zip-code")}
                </th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>
                  {" "}
                  {t("Sqm")}
                </th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>
                  {" "}
                  {t("Bedrooms")}
                </th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>
                  {" "}
                  {t("Units")}
                </th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>
                  {" "}
                  {t("Index")}
                </th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>
                  {" "}
                  {t("table_header_city")}
                </th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>
                  {" "}
                  {t("table_header_type")}
                </th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>
                  {" "}
                  {t("table_header_price")}
                </th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>
                  {" "}
                  {t("status") || "Status"}
                </th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}></th>{" "}
                {/* Actions column header */}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" style={{ textAlign: 'center', padding: '20px' }}>
                    {t("loading_properties_message") || "Loading properties..."}
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="12" style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
                    {error}
                  </td>
                </tr>
              ) : currentProperties.length > 0 ? (
                currentProperties.map((property, index) => {
                  const rowClassName =
                    index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd; // New classes for row colors
                  return (
                    <tr
                      key={property.id}
                      className={`${styles.tableRow} ${rowClassName} ${styles.clickableRow}`} // clickableRow for cursor
                    >
                      {/* Link wrapping multiple <td>s for navigation */}
                      <Link
                        to={`/EditOrView/${property.id}`}
                        className={styles.tableRowLink} // New class for display: contents
                      >
                        <td
                          className={`${styles.tableCell} ${styles.inheritBackground}`}
                        >
                        {property?.displayId == null ? "-" : property.displayId}
                        </td>
                        {/* <td
                          className={`${styles.tableCell} ${styles.inheritBackground}`}
                        >
                          {property.id.substring(0, 8)}...
                        </td> */}
                        <td
                          className={`${styles.tableCell} ${styles.inheritBackground}`}
                        >
                          {`${property.propertyTitle}`}
                        </td>
                        <td
                          className={`${styles.tableCell} ${styles.inheritBackground}`}
                        >
                          {property.PropertyAddresses?.[0]?.zipCode ||
                            t("not_available_abbr")}
                        </td>
                        <td
                          className={`${styles.tableCell} ${styles.inheritBackground}`}
                          style={{ textAlign: 'center' }}
                        >
                          {`${property.squareMeters}`}
                        </td>
                        <td
                          className={`${styles.tableCell} ${styles.inheritBackground}`}
                          style={{ textAlign: 'center' }}
                        >
                          {property.propertyType !== 'multifamily' && property.propertyType !== 'plot'
                            ? (property.bedrooms || '-')
                            : '-'}
                        </td>
                        <td
                          className={`${styles.tableCell} ${styles.inheritBackground}`}
                          style={{ textAlign: 'center' }}
                        >
                          {property.propertyType === 'multifamily'
                            ? (property.units || '-')
                            : '-'}
                        </td>
                        <td
                          className={`${styles.tableCell} ${styles.inheritBackground}`}
                          style={{ textAlign: 'center' }}
                        >
                          {property.propertyType === 'plot'
                            ? (property.index || '-')
                            : '-'}
                        </td>
                        <td
                          className={`${styles.tableCell} ${styles.inheritBackground}`}
                          style={{ textAlign: 'center' }}
                        >
                          {property.PropertyAddresses?.[0]?.city ||
                            t("not_available_abbr")}
                        </td>
                        <td
                          className={`${styles.tableCell} ${styles.inheritBackground}`}
                          style={{ textAlign: 'center' }}
                        >
                          {property.propertyType.toUpperCase()}
                        </td>
                        <td
                          className={`${styles.tableCell} ${styles.inheritBackground}`}
                          style={{ textAlign: 'center' }}
                        >
                          {property.price ? `${formatWithApostrophe(property.price)} ${property.currency || 'CHF'}` : t("not_available_abbr")}
                        </td>
                        <td
                          className={`${styles.tableCell} ${styles.inheritBackground}`}
                        >
                          <span 
                            style={{
                              color: property.status === 'published' ? '#29b185' : '#ff6b6b',
                              fontWeight: '600',
                              textTransform: 'capitalize'
                            }}
                          >
                            {property.status || 'Inactive'}
                          </span>
                        </td>
                      </Link>

                      {/* Action buttons column - outside the main Link */}
                      <td
                        className={`${styles.tableCell} ${styles.whiteBackground}`}
                      >
                        <div className={styles.actionButtons}>
                          <button
                            className={styles.iconButton}
                            title={t("edit_button_title")}
                          >
                            <Link
                              to={`/EditOrView/${property.id}`}
                              className={styles.iconButton}
                              title={t("edit_or_view_button_title")}
                            >
                              <img
                                src={editIcon}
                                alt={t("edit_alt_text")}
                                className={styles.iconImageEye}
                              />
                            </Link>
                          </button>
                          <button
                            className={styles.iconButton}
                            title={t("edit_button_title")}
                          >
                            <Link
                              to={isOfferUser ? `/OfferProperty/${property.id}` : `/SearchProperty/${property.id}`}
                              className={styles.iconButton}
                              title={t("edit_button_title")}
                            >
                              <img
                                src={editPenIcon}
                                alt={t("edit_alt_text")}
                                className={styles.iconImage}
                              />
                            </Link>
                          </button>
                          <button
                            className={styles.iconButton}
                            onClick={() => handleDelete(property.id)}
                            title={t("delete_button_title")}
                          >
                            <img
                              src={deleteIcon}
                              alt={t("delete_alt_text")}
                              className={styles.iconImage}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="12"
                    className={`${styles.tableCell} ${styles.noPropertiesFound}`}
                  >
                    {t("no_properties_found")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className={styles.paginationContainer}>
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={styles.paginationButton}
                  >
                    <img src={chevronLeft} alt="Previous" style={{ width: '20px', height: '20px' }} />
                  </button>
                  {(() => {
                    const maxVisible = 5;
                    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                    let end = start + maxVisible - 1;
                    if (end > totalPages) {
                      end = totalPages;
                      start = Math.max(1, end - maxVisible + 1);
                    }
                    return Array.from({ length: end - start + 1 }, (_, i) => start + i).map((number) => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`${styles.paginationButton} ${currentPage === number ? styles.activePaginationButton : ""}`}
                      >
                        {number}
                      </button>
                    ));
                  })()}
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={styles.paginationButton}
                  >
                    <img src={chevronRight} alt="Next" style={{ width: '20px', height: '20px' }} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showConfirmModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>{t("confirm_delete_title")}</h3>
            <p className={styles.modalMessage}>{t("confirm_delete_message")}</p>
            <div className={styles.modalButtons}>
              <button
                className={`${styles.modalButton} ${styles.modalButtonConfirm}`}
                onClick={confirmDelete}
              >
                {t("yes_delete_button")}
              </button>
              <button
                className={`${styles.modalButton} ${styles.modalButtonCancel}`}
                onClick={cancelDelete}
              >
                {t("cancel_button")}
              </button>
            </div>
          </div>
        </div>
      )}
      {isOfferUser && (
        <BulkUploadModal
          isOpen={showBulkUploadModal}
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={fetchProperties}
        />
      )}
    </div>
  );
};

// const styles = {
//     container: {
//         display: 'flex',
//         minHeight: '100vh',
//         fontFamily: 'Inter, sans-serif',
//     },
//     leftPanel: {
//         borderRadius: '4px',
//         width: '237px', // Fixed width for the left panel. Adjust as needed.
//         flexShrink: 0, // Prevents the left panel from shrinking
//         padding: '15px', // Slightly increased padding for better spacing
//         background: 'white',
//         boxShadow: '0 4px 12px rgba(0,0,0,0.08)', // Added shadow for consistency
//         borderRight: '1px solid #ddd',
//         marginRight: '5px', // Space between left panel and main content
//         alignSelf: 'flex-start', // Aligns to the top
//         display: 'flex',
//         flexDirection: 'column',
//         alignItems: 'center',
//         overflowY: 'auto',
//     },
//     rightPanel: {
//         flex: 1.5,
//         padding: '20px',
//         background: '#fff',
//         marginLeft: '8px',
//         marginTop: '10px',
//     },
//     pageHeader: {
//         fontSize: '20px',
//         fontWeight: '700',
//         color: 'rgba(41, 177, 133, 1)',
//         marginBottom: '20px',
//     },
//     trendingHeader: {
//         fontSize: '22px',
//         fontWeight: '700',
//         color: 'rgba(41, 177, 133, 1)',
//         marginBottom: '10px',
//         marginTop: '15px',
//         textAlign: 'center',
//         textShadow: '1px 1px 2px rgba(0, 0, 0, 0.28)'
//     },
//     propertyCard: {
//         marginBottom: '20px',
//         backgroundColor: 'White',
//         borderRadius: '8px',
//         boxShadow: '0px 1px 3px 1px #00000026',
//     },
//     propertyImage: {
//         height: '150px',
//         backgroundColor: '#ddd',
//         marginBottom: '10px',
//         borderRadius: '4px',
//         overflow: 'hidden'
//     },
//     propertyImage2: {
//         height: '200px',
//         backgroundColor: '#ddd',
//         marginBottom: '10px',
//         borderRadius: '4px',
//         overflow: 'hidden'
//     },
//     propertyImg: {
//         width: '100%',
//         height: '100%',
//         objectFit: 'cover',
//         borderRadius: '4px'
//     },
//     propertyImg2: {
//         width: '100%',
//         height: '120%',
//         objectFit: 'cover',
//         borderRadius: '4px'
//     },
//     propertyText: {
//         fontSize: '16px',
//         color: '#555',
//         width: '216px',
//         height: '46px',
//         marginLeft: '10px',
//     },
//     propertyTextSpan: {
//         color: 'rgba(41, 177, 133, 1)',
//         fontWeight: 600,
//         fontSize: '16px'
//     },
//     tabsContainer: {
//         display: 'flex',
//         marginBottom: '10px',
//     },
//     tabButton: {
//         padding: '10px 64px',
//         backgroundColor: 'rgba(204, 204, 204, 0.4)',
//         border: 'none',
//         cursor: 'pointer',
//         fontSize: '20px',
//         fontWeight: '400',
//         color: '#555',
//         position: 'relative'
//     },
//     activeTab: {
//         color: 'white',
//         backgroundColor: 'rgba(41, 177, 133, 1)',
//     },
//     inactiveTab: {
//         color: 'black'
//     },
//     tableContainer: {
//         width: '100%',
//         overflowX: 'auto'
//     },
//     searchTable: {
//         width: '100%',
//         borderCollapse: 'separate',
//         borderSpacing: '0 15px',
//     },
//     tableHeader: {
//         padding: '12px 15px',
//         textAlign: 'left',
//         fontWeight: '400',
//         color: 'rgba(41, 177, 133, 1)',
//         fontSize: '20px'
//     },
//     tableRow: {
//         borderBottom: '1px solid #eee',

//     },
//     tableCell: {
//         padding: '3px 10px',
//         fontSize: '20px',
//         fontWeight: '300',
//         color: 'rgba(0, 0, 0, 1)',
//         verticalAlign: 'middle',
//     },
//     actionButtons: {
//         display: 'flex',
//         gap: '8px',
//     },
//     iconButton: {
//         background: 'none',
//         border: 'none',
//         cursor: 'pointer',
//         padding: '3px'
//     },
//     iconImage: {
//         width: '24px',
//         height: '25px'
//     },
//     loadingMessage: {
//         textAlign: 'center',
//         padding: '50px',
//         fontSize: '24px',
//         color: 'rgba(41, 177, 133, 1)',
//     },
//     paginationContainer: {
//         display: 'flex',
//         justifyContent: 'center',
//         marginTop: '100px',
//         gap: '10px',
//         // Removed your large paddingLeft, let flex-start do its job
//     },
//     paginationButton: {
//         padding: '8px 16px',
//         border: '1px solid #ccc',
//         borderRadius: '5px',
//         backgroundColor: '#f0f0f0',
//         cursor: 'pointer',
//         fontSize: '16px',
//         transition: 'background-color 0.3s ease',
//         '&:hover': {
//             backgroundColor: '#e0e0e0',
//         },
//         '&:disabled': {
//             cursor: 'not-allowed',
//             opacity: 0.5,
//         },
//     },
//     activePaginationButton: {
//         backgroundColor: 'rgba(41, 177, 133, 1)',
//         color: 'white',
//         borderColor: 'rgba(41, 177, 133, 1)',
//     },
//     errorMessage: {
//         textAlign: 'center',
//         padding: '50px',
//         fontSize: '24px',
//         color: 'red',
//     },
//     modalOverlay: {
//         position: 'fixed',
//         top: 0,
//         left: 0,
//         right: 0,
//         bottom: 0,
//         backgroundColor: 'rgba(0, 0, 0, 0.6)', // Dark overlay
//         display: 'flex',
//         justifyContent: 'center',
//         alignItems: 'center',
//         zIndex: 1000, // Ensure it's on top of other content
//     },
//     modalContent: {
//         backgroundColor: 'white',
//         padding: '30px',
//         borderRadius: '10px',
//         boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)', // Soft shadow
//         textAlign: 'center',
//         width: '90%', // Responsive width
//         maxWidth: '400px', // Max width for larger screens
//         display: 'flex',
//         flexDirection: 'column',
//         gap: '20px', // Space between elements
//     },
//     modalTitle: {
//         fontSize: '22px',
//         fontWeight: 'bold',
//         color: '#333',
//         marginBottom: '10px',
//     },
//     modalMessage: {
//         fontSize: '16px',
//         color: '#555',
//         marginBottom: '20px',
//     },
//     modalButtons: {
//         display: 'flex',
//         justifyContent: 'center',
//         gap: '15px',
//     },
//     modalButton: {
//         padding: '10px 20px',
//         borderRadius: '5px',
//         border: 'none',
//         cursor: 'pointer',
//         fontSize: '16px',
//         fontWeight: 'bold',
//         transition: 'background-color 0.2s ease', // Smooth hover
//     },
//     modalButtonConfirm: {
//         backgroundColor: '#dc3545', // Red for delete action
//         color: 'white',
//         // Add hover effect if using CSS modules or styled-components
//         // '&:hover': { backgroundColor: '#c82333' }
//     },
//     modalButtonCancel: {
//         backgroundColor: '#29B185', // Gray for cancel
//         color: 'white',
//         // Add hover effect if using CSS modules or styled-components
//         // '&:hover': { backgroundColor: '#5a6268' }
//     },
// };

export default MyProperties;
